import {
  ButtonContainer,
  ControlBar,
  ControlButtonGroup,
  HighlightPoint,
  InfoDisplayContainer,
  ProgressBar,
  ProgressFill,
  SettingsContainer,
  SettingsToggleButton,
  SettingsTogglesList,
  SettingsTogglesWrapper,
} from "@Components/game/session-results/feedback/styled-components";
import HapticsButtonWrapper from "@Components/generic/haptics-button-wrapper";
import Icon from "@Components/generic/icon";
import { faXmark } from "@fortawesome/pro-regular-svg-icons";
import { faBackwardStep, faExpand, faForwardStep, faPause, faPlay, faRotateRight, faSidebarFlip, faSliders } from "@fortawesome/pro-solid-svg-icons";
import { formatReplayTime } from "@hooks/common/session/decision-score-rows";
import { getDecisionCardTime } from "@hooks/common/session/replay-decision-utils";
import { SessionFeedbackModel } from "@hooks/game/activity/session/use-session-feedback-model";
import useI18n from "@i18n";
import { Box, CircularProgress, Skeleton, Typography, useTheme } from "@mui/material";
import "@pixi/events";

interface IVideoProgressBar {
  feedbackModel: SessionFeedbackModel;
}

const getProgressPercentage = (progress: number, duration: number) => {
  if (!Number.isFinite(progress) || !Number.isFinite(duration) || duration <= 0) {
    return 0;
  }

  return Math.max(0, Math.min(100, (progress / duration) * 100));
};

const VideoProgressBar = ({ feedbackModel }: IVideoProgressBar) => {
  const { i18n } = useI18n();
  const theme = useTheme();

  const { replay, playback, actions } = feedbackModel;

  const duration = playback.videoDuration;
  const progressPercentage = getProgressPercentage(playback.progress, duration);
  const canGoPrevious = replay.decisions && playback.highlightPointIndex > 0;
  const canGoNext = replay.decisions && playback.highlightPointIndex < (replay.decisions?.length || 0) - 1;

  const formattedCurrentTime = formatReplayTime(playback.progress);
  const formattedDuration = formatReplayTime(duration);

  return (
    <Box
      padding={1}
      sx={{
        flex: "0 0 12.5%",
        minHeight: 0,
        boxSizing: "border-box",
        display: "flex",
      }}
    >
      <ControlBar>
        {/* Navigation Buttons */}
        <ControlButtonGroup>
          {canGoPrevious ? (
            <HapticsButtonWrapper>
              <ButtonContainer
                onClick={actions.handlePreviousDecision}
                style={{ opacity: canGoPrevious ? 1 : 0.5, cursor: canGoPrevious ? "pointer" : "not-allowed" }}
              >
                <Icon icon={faBackwardStep} size="lg" color="white" />
              </ButtonContainer>
            </HapticsButtonWrapper>
          ) : (
            <ButtonContainer
              onClick={actions.handlePreviousDecision}
              style={{ opacity: canGoPrevious ? 1 : 0.5, cursor: canGoPrevious ? "pointer" : "not-allowed" }}
            >
              <Icon icon={faBackwardStep} size="lg" color="white" />
            </ButtonContainer>
          )}

          <HapticsButtonWrapper>
            <ButtonContainer onClick={actions.handlePlayPause}>
              <Icon
                icon={duration > 0 && playback.progress >= duration ? faRotateRight : playback.isPlaying ? faPause : faPlay}
                size="lg"
                color="white"
              />
            </ButtonContainer>
          </HapticsButtonWrapper>

          {canGoNext ? (
            <HapticsButtonWrapper>
              <ButtonContainer
                onClick={actions.handleNextDecision}
                style={{ opacity: canGoNext ? 1 : 0.5, cursor: canGoNext ? "pointer" : "not-allowed" }}
              >
                <Icon icon={faForwardStep} size="lg" color="white" />
              </ButtonContainer>
            </HapticsButtonWrapper>
          ) : (
            <ButtonContainer
              onClick={actions.handleNextDecision}
              style={{ opacity: canGoNext ? 1 : 0.5, cursor: canGoNext ? "pointer" : "not-allowed" }}
            >
              <Icon icon={faForwardStep} size="lg" color="white" />
            </ButtonContainer>
          )}
        </ControlButtonGroup>

        {/* Decision Counter */}
        <InfoDisplayContainer>
          {replay.replayData && replay.decisions?.length ? (
            <Typography variant="textLg400" color="white" noWrap>
              {i18n("session-results.results.decision-number", {
                current: playback.highlightPointIndex + 1,
                total: replay.decisions?.length || 0,
              })}
            </Typography>
          ) : replay.areDecisionsLoading ? (
            <Skeleton variant="text" width={44} height={24} />
          ) : null}
        </InfoDisplayContainer>

        {/* Progress Bar */}
        <ProgressBar onClick={actions.handleProgressClick}>
          {/* Highlight points */}
          {Array.isArray(replay.decisions) &&
            replay.decisions.map((decision, index) => {
              return (
                <HighlightPoint
                  key={`point-${index}`}
                  startTime={getDecisionCardTime(decision, playback.selectedCard)}
                  duration={duration}
                  isSelected={index === playback.highlightPointIndex}
                  onClick={() => actions.handleHighlightClick(index)}
                />
              );
            })}

          {replay.replayData && (
            <ProgressFill
              sx={{
                width: `${progressPercentage}%`,
              }}
            />
          )}
        </ProgressBar>

        {/* Time Display */}
        <InfoDisplayContainer>
          {replay.replayData ? (
            <Typography variant="textS400" color="white" noWrap>
              {i18n("session-results.video.time-display", { currentTime: formattedCurrentTime, duration: formattedDuration })}
            </Typography>
          ) : (
            <Skeleton variant="text" width={66} height={24} />
          )}
        </InfoDisplayContainer>

        {/* Settings Button with Toggles */}
        <SettingsContainer>
          <SettingsTogglesWrapper>
            <SettingsTogglesList>
              <HapticsButtonWrapper>
                <SettingsToggleButton
                  isActive={playback.showVisionCone}
                  showSettings={playback.showSettings}
                  animationIndex={0}
                  onClick={actions.handleToggleVisionCone}
                >
                  <Typography variant="textS600" noWrap>
                    {i18n("session-results.video.vision-cone")}
                  </Typography>
                </SettingsToggleButton>
              </HapticsButtonWrapper>

              <HapticsButtonWrapper>
                <SettingsToggleButton
                  isActive={playback.showHeatmap}
                  showSettings={playback.showSettings}
                  animationIndex={1}
                  onClick={() => actions.handleToggleHeatmap(!playback.showHeatmap)}
                >
                  <Typography variant="textS600" noWrap>
                    {i18n("session-results.video.heatmap")}
                  </Typography>
                </SettingsToggleButton>
              </HapticsButtonWrapper>
            </SettingsTogglesList>
          </SettingsTogglesWrapper>

          <HapticsButtonWrapper>
            <ButtonContainer onClick={actions.handleToggleSettings}>
              <Icon icon={playback.showSettings ? faXmark : faSliders} size="lg" color="white" />
            </ButtonContainer>
          </HapticsButtonWrapper>
        </SettingsContainer>

        <HapticsButtonWrapper>
          <ButtonContainer onClick={actions.handleToggleDecisionPanel}>
            <Icon icon={faSidebarFlip} size="lg" color={playback.isDecisionPanelExpanded ? theme.palette.dsPrimary.v300 : "white"} />
          </ButtonContainer>
        </HapticsButtonWrapper>

        {/* Fullscreen Button */}
        {playback.isUnityReplayReady ? (
          <HapticsButtonWrapper>
            <ButtonContainer onClick={actions.handleFullscreen}>
              <Icon icon={faExpand} size="lg" color="white" />
            </ButtonContainer>
          </HapticsButtonWrapper>
        ) : (
          <ButtonContainer style={{ cursor: "not-allowed" }}>
            <CircularProgress size={20} sx={{ color: "white" }} />
          </ButtonContainer>
        )}
      </ControlBar>
    </Box>
  );
};

export default VideoProgressBar;
